import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  keyboardConsumed,
  useDOMNodeSize,
  useWindowEvent,
  useWindowEventConditional,
  useWindowInnerSize
} from './hooks';
import type { Vec2 } from './vec2';

export {
  useWindowEvent,
  useWindowEventConditional,
  useDOMNodeSize,
  useWindowInnerSize,
  keyboardConsumed
};

const hasWindow = typeof window !== 'undefined';

const isMacLike = /(Mac|iPhone|iPod|iPad)/i.test(
  hasWindow ? (window.navigator?.platform ?? '') : ''
);

/**
 * Get keyboard shortcut text for a key combination.
 * The formatting is dependant on the detected OS.
 */
export function shortcutDesc(key: string, mod = false, shift = false) {
  if (isMacLike) {
    return `${mod ? '⌘' : ''}${shift ? '⇧' : ''}${key}`;
  } else {
    return `${mod ? 'Ctrl+' : ''}${shift ? 'Shift+' : ''}${key}`;
  }
}

export interface Size {
  width: number;
  height: number;
}

export interface EventPosition {
  point: Vec2;
  initial: Vec2;
  relative: Vec2;
  drag: Vec2;
  bounds: Vec2 & { width: number; height: number };
}

function computePosition(
  target: Element,
  event: PointerEvent,
  initial: Vec2
): EventPosition {
  const bounds = target.getBoundingClientRect();
  return {
    bounds: {
      width: bounds.width,
      height: bounds.height,
      x: bounds.x,
      y: bounds.y
    },
    point: {
      x: event.clientX,
      y: event.clientY
    },
    initial,
    drag: {
      x: event.clientX - initial.x,
      y: event.clientY - initial.y
    },
    relative: {
      x: bounds.width > 0 ? (event.clientX - bounds.x) / bounds.width : 0,
      y: bounds.height > 0 ? (event.clientY - bounds.y) / bounds.height : 0
    }
  };
}

type PointerEventType = 'start' | 'move' | 'stop';

/**
 * Register for pointer dragging events.
 *
 * @param handler callback on any pointer event
 * @param buttonMask declare which buttons to look for. The value represents a `PointerEvent.buttons` mask.
 * @returns events object to spread onto the element, and dragging state
 */
export function usePointer(
  handler: (
    pos: EventPosition,
    eventType: PointerEventType,
    event: PointerEvent
  ) => void,
  buttonMask = 1
) {
  const [dragging, setDragging] = useState(false);
  const trackedPointerRef = useRef<number | null>(null);
  const trackedElementRef = useRef<Element | null>(null);
  const grabPosRef = useRef<Vec2 | null>(null);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  function doStop(e: PointerEvent) {
    trackedPointerRef.current = null;
    setDragging(false);
    if (trackedElementRef.current !== null && grabPosRef.current !== null) {
      handlerRef.current(
        computePosition(trackedElementRef.current, e, grabPosRef.current),
        'stop',
        e
      );
      trackedElementRef.current = null;
    }
  }

  function doMove(e: PointerEvent) {
    if (trackedElementRef.current !== null && grabPosRef.current !== null) {
      handlerRef.current(
        computePosition(trackedElementRef.current, e, grabPosRef.current),
        'move',
        e
      );
    }
  }

  useEffect(() => {
    function onPointerUp(e: PointerEvent) {
      if (trackedPointerRef.current === e.pointerId) {
        e.preventDefault();
        doStop(e);
      }
    }
    function onPointerMove(e: PointerEvent) {
      if (trackedPointerRef.current === e.pointerId) {
        e.preventDefault();
        if ((e.buttons & buttonMask) !== 0) {
          doMove(e);
        } else {
          doStop(e);
        }
      }
    }
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointermove', onPointerMove);
    return () => {
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [buttonMask]);

  const events = {
    onPointerDown(e: ReactPointerEvent) {
      if ((e.buttons & buttonMask) === 0) {
        return;
      }
      if (
        trackedPointerRef.current === null &&
        e.currentTarget instanceof Element
      ) {
        e.preventDefault();
        e.stopPropagation();
        trackedPointerRef.current = e.pointerId;
        trackedElementRef.current = e.currentTarget;
        grabPosRef.current = { x: e.clientX, y: e.clientY };
        setDragging(true);
        handlerRef.current(
          computePosition(
            trackedElementRef.current,
            e.nativeEvent,
            grabPosRef.current
          ),
          'start',
          e.nativeEvent
        );
      }
    }
  };

  return { events, dragging };
}
